import { useState } from 'react'
import { Input, Toggle } from '@/components/ui'
import type { LetterPatch, PriceLetter } from '../api/letterTypes'

interface FieldProps {
  label: string
  value: string
  disabled: boolean
  type?: 'text' | 'date'
  placeholder?: string
  onCommit: (next: string) => void
}

/** Local state, saved on blur. Seeded once so a background refresh never overwrites typing. */
function Field({ label, value, disabled, type = 'text', placeholder, onCommit }: FieldProps) {
  const [draft, setDraft] = useState(value)
  return (
    <label className="flex flex-col gap-1">
      <span className="text-role-section-label">{label}</span>
      <Input
        type={type}
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => draft !== value && onCommit(draft)}
      />
    </label>
  )
}

export interface LetterHeaderFormProps {
  letter: PriceLetter
  onPatch: (changes: LetterPatch) => void
}

export function LetterHeaderForm({ letter, onPatch }: LetterHeaderFormProps) {
  const locked = letter.status === 'finalized'
  const [address, setAddress] = useState((letter.address_lines ?? []).join('\n'))
  const orNull = (s: string) => (s.trim() === '' ? null : s.trim())

  return (
    <section className="flex flex-col gap-3 rounded-sm border border-border-default bg-surface p-4">
      <h2 className="text-role-section-label m-0">1. Letter details</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field
          label="Letter date"
          type="date"
          disabled={locked}
          value={letter.letter_date ?? ''}
          onCommit={(v) => v && onPatch({ letter_date: v })}
        />
        <Field
          label="Effective date"
          type="date"
          disabled={locked}
          value={letter.effective_date ?? ''}
          onCommit={(v) => onPatch({ effective_date: orNull(v) })}
        />
        <Field
          label="Greeting (Dear …)"
          disabled={locked}
          placeholder={letter.partner_name}
          value={letter.greeting_name ?? ''}
          onCommit={(v) => onPatch({ greeting_name: orNull(v) })}
        />
        <Field
          label="Signatory name"
          disabled={locked}
          value={letter.signatory_name ?? ''}
          onCommit={(v) => onPatch({ signatory_name: orNull(v) })}
        />
        <Field
          label="Signatory title"
          disabled={locked}
          value={letter.signatory_title ?? ''}
          onCommit={(v) => onPatch({ signatory_title: orNull(v) })}
        />
        <Field
          label="Signatory email"
          disabled={locked}
          value={letter.signatory_email ?? ''}
          onCommit={(v) => onPatch({ signatory_email: orNull(v) })}
        />
        <Field
          label="Signatory phone"
          disabled={locked}
          value={letter.signatory_phone ?? ''}
          onCommit={(v) => onPatch({ signatory_phone: orNull(v) })}
        />
        <div />
        <Field
          label="Rep name (footer)"
          disabled={locked}
          value={letter.rep_name ?? ''}
          onCommit={(v) => onPatch({ rep_name: orNull(v) })}
        />
        <Field
          label="Rep email (footer)"
          disabled={locked}
          value={letter.rep_email ?? ''}
          onCommit={(v) => onPatch({ rep_email: orNull(v) })}
        />
        <Field
          label="Rep phone (footer)"
          disabled={locked}
          value={letter.rep_phone ?? ''}
          onCommit={(v) => onPatch({ rep_phone: orNull(v) })}
        />
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-role-section-label">Customer address (one line per row)</span>
        <textarea
          rows={4}
          value={address}
          disabled={locked}
          onChange={(e) => setAddress(e.target.value)}
          onBlur={() => {
            const lines = address.split('\n').map((l) => l.trim()).filter(Boolean)
            if (lines.join('\n') !== (letter.address_lines ?? []).join('\n')) {
              onPatch({ address_lines: lines })
            }
          }}
          className="w-full resize-none rounded-sm border border-border-default bg-surface p-2 text-md text-primary disabled:bg-surface-sunk"
        />
      </label>
      <div className="flex flex-col gap-1">
        <Toggle
          label="Show tariff surcharge columns (Tariff %, Tariff $, Total) on this letter"
          checked={letter.show_tariff}
          disabled={locked}
          onChange={(e) => onPatch({ show_tariff: e.target.checked })}
        />
        <span className="text-role-metadata">
          Off by default. Turn on only for a customer who should see tariffs itemized.
        </span>
      </div>
    </section>
  )
}
